import { ROOM_KEYS } from "../Components/PassengersData/PassengersData";
import { applyLdsHandlingFeeAndCardToFareDetails } from "../pricing/vacationPackageLdsAdapter";
import { resolveVacationPackageRoomMarkupIls } from "./vacationPackageRoomAgentMarkup";

const MANUAL_ADD_ROOM_PRICE_SOURCE = "manual_add_room";

function isManualRoom(room, slot) {
  return (
    room?.addedFromRoomOptions === true ||
    room?.priceSource === MANUAL_ADD_ROOM_PRICE_SOURCE ||
    slot?.addedFromRoomOptions === true
  );
}

function readSelectedAddRoomPriceIls(room, slot) {
  const n = Number(room?.selectedAddRoomPriceIls ?? slot?.selectedAddRoomPriceIls);
  return Number.isFinite(n) && n > 0 ? _round2(n) : null;
}

function readPwccFromRoom(room) {
  const n = Number(room?.ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee);
  return Number.isFinite(n) && n > 0 ? _round2(n) : null;
}

const LS_MANUAL_MARKUP = "DEBUG_VP_MANUAL_ROOM_MARKUP";

/** @type {Record<string, string>} */
const _lastTraceFp = Object.create(null);

function _isDev() {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

function _verbose() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_MANUAL_MARKUP) === "1";
  } catch {
    return false;
  }
}

function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/** Base PWC without per-room markup / display fees (frozen at add-room). */
export function readManualRoomBasePriceIls(room, slot = null) {
  const r = room ?? slot?.room;
  if (!r) return null;
  const snap = r.pricingSnapshot ?? slot?.pricingSnapshot;
  const fromSnap = _num(snap?.basePriceWithCommissionIls);
  if (fromSnap != null && fromSnap > 0) return fromSnap;

  const stored = _num(r.selectedAddRoomBasePriceIls ?? slot?.selectedAddRoomBasePriceIls);
  if (stored != null && stored > 0) return stored;

  const tf = r.ourFareDetails?.ourTotalFare ?? r[ROOM_KEYS.OUR_FARE_DETAILS]?.ourTotalFare;
  if (tf) {
    const initial = _num(tf.initialPrice);
    const hr = _num(tf.hrCommission);
    if (initial != null && initial > 0 && hr != null && hr >= 0) {
      return _round2(initial + hr);
    }
  }
  return readSelectedAddRoomPriceIls(r, slot);
}

const PWC_INVARIANT_TOLERANCE_ILS = 1;

export function logManualAddRoomPwcInvariantIfFailed(slot, tf) {
  if (!_isDev() || !tf) return;
  const initial = _num(tf.initialPrice);
  const hr = _num(tf.hrCommission);
  const pwc = _num(tf.priceWithCommission);
  if (initial == null || hr == null || pwc == null) return;
  const expectedPwc = _round2(initial + hr);
  if (Math.abs(pwc - expectedPwc) <= PWC_INVARIANT_TOLERANCE_ILS) return;
  const room = slot?.room;
  const snap = room?.pricingSnapshot ?? slot?.pricingSnapshot ?? null;
  // eslint-disable-next-line no-console
  console.warn("MANUAL_ADD_ROOM_PWC_INVARIANT_FAILED", {
    roomType: room?.roomType ?? room?.selectedAddRoomRoomType ?? null,
    dealSeq: room?.selectedAddRoomDealSeq ?? room?.["@seqNum"] ?? null,
    initialPrice: initial,
    hrCommission: hr,
    priceWithCommission: pwc,
    expectedPwc,
    selectedAddRoomBasePriceIls:
      room?.selectedAddRoomBasePriceIls ?? slot?.selectedAddRoomBasePriceIls ?? null,
    selectedAddRoomPriceIls:
      room?.selectedAddRoomPriceIls ?? slot?.selectedAddRoomPriceIls ?? null,
    pricingSnapshot: snap,
  });
}

/**
 * Re-apply per-room fixed markup (+ CC) on manual-add room fare from frozen base.
 */
export function recomputeManualSlotOurFareDetails(
  slot,
  { handleFeeData, numberOfRooms, getCreditCardFeePercent }
) {
  const room = slot?.room;
  const fd = room?.ourFareDetails ?? room?.[ROOM_KEYS.OUR_FARE_DETAILS];
  if (!fd?.ourTotalFare) return null;

  const baseManualRoomPriceIls = readManualRoomBasePriceIls(room, slot);
  if (baseManualRoomPriceIls == null) return null;

  const cloned = JSON.parse(JSON.stringify(fd));
  const tf = cloned.ourTotalFare;
  tf.priceWithCommission = baseManualRoomPriceIls;
  tf.handleFee = 0;
  tf.priceWithHandleFee = baseManualRoomPriceIls;
  tf.creditCardFee = 0;
  tf.priceWithCommissionAndCreditCardFee = null;
  tf.priceWithCreditCardFee = null;
  logManualAddRoomPwcInvariantIfFailed(slot, tf);

  const roomLevelHandleFeeIls = resolveVacationPackageRoomMarkupIls(slot, handleFeeData);
  const { ourFareDetails: updated } = applyLdsHandlingFeeAndCardToFareDetails({
    ourFareDetails: cloned,
    numberOfRooms: Math.max(1, Number(numberOfRooms) || 1),
    handleFeeData,
    getCreditCardFeePercent,
    perRoomFixedMarkupIls: roomLevelHandleFeeIls,
  });

  const priceAfterMarkup = readPwccFromRoom({ ourFareDetails: updated });
  const appliedHandle = _num(updated?.ourTotalFare?.handleFee) ?? roomLevelHandleFeeIls;

  return {
    updated,
    baseManualRoomPriceIls,
    roomLevelHandleFeeIls: appliedHandle,
    priceBeforeMarkup: baseManualRoomPriceIls,
    priceAfterMarkup,
  };
}

/** Mutates slot + room with recomputed fare and display fields (does not overwrite selectedAddRoomPriceIls). */
export function applyManualRoomMarkupFieldsToSlot(slot, recomputeResult) {
  if (!slot || !recomputeResult?.updated) return;
  const {
    updated,
    baseManualRoomPriceIls,
    roomLevelHandleFeeIls,
    priceAfterMarkup,
  } = recomputeResult;

  if (!slot.room) slot.room = {};
  slot.room.ourFareDetails = updated;
  slot.room[ROOM_KEYS.OUR_FARE_DETAILS] = updated;

  if (slot.room.selectedAddRoomBasePriceIls == null && baseManualRoomPriceIls != null) {
    slot.room.selectedAddRoomBasePriceIls = baseManualRoomPriceIls;
  }
  if (slot.selectedAddRoomBasePriceIls == null && baseManualRoomPriceIls != null) {
    slot.selectedAddRoomBasePriceIls = baseManualRoomPriceIls;
  }

  slot.room.roomLevelHandleFeeIls = roomLevelHandleFeeIls;
  slot.roomLevelHandleFeeIls = roomLevelHandleFeeIls;
  slot.room.displayedPriceWithMarkupIls = priceAfterMarkup;
  slot.displayedPriceWithMarkupIls = priceAfterMarkup;
  if (slot.room.pricingSnapshot) {
    slot.pricingSnapshot = slot.room.pricingSnapshot;
  }
}

/**
 * @param {object|null|undefined} room
 * @param {object|null|undefined} slot
 * @param {object|null|undefined} handleFeeData
 */
export function resolveManualRoomDisplayPriceIls(room, slot, handleFeeData) {
  const displayed = _num(
    room?.displayedPriceWithMarkupIls ?? slot?.displayedPriceWithMarkupIls
  );
  if (displayed != null && displayed > 0) {
    return {
      valid: true,
      priceIls: displayed,
      via: "manual_add_room_with_room_markup",
    };
  }

  const pwcc = readPwccFromRoom(room);
  if (pwcc != null && pwcc > 0) {
    return {
      valid: true,
      priceIls: pwcc,
      via: "manual_add_room_with_room_markup",
    };
  }

  const base = readManualRoomBasePriceIls(room, slot);
  const markup = resolveVacationPackageRoomMarkupIls(slot, handleFeeData);
  if (base != null && markup != null && handleFeeData?.type === "+") {
    return {
      valid: true,
      priceIls: _round2(base + markup),
      via: "manual_add_room_base_plus_markup_estimate",
    };
  }

  const legacy = readSelectedAddRoomPriceIls(room, slot);
  if (legacy != null) {
    return { valid: true, priceIls: legacy, via: "selectedAddRoomPriceIls" };
  }
  return { valid: false, priceIls: null, via: null };
}

/**
 * @param {object} payload
 * @param {{ force?: boolean }} [opts]
 */
export function logVpManualRoomMarkupTrace(payload, opts = {}) {
  if (!_isDev() || !payload) return;
  const key = String(payload.roomIndex ?? "?");
  const fp = JSON.stringify(payload);
  if (!opts.force && !_verbose() && _lastTraceFp[key] === fp) return;
  _lastTraceFp[key] = fp;
  // eslint-disable-next-line no-console
  console.info("[VP_MANUAL_ROOM_MARKUP_TRACE]", payload);
}
